import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';
import { SkillCardNew } from './SkillCardNew';
import type { SkillMeta } from './types';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

interface Props {
  search: string;
  onDetails: (s: SkillMeta) => void;
  onInstall: (s: SkillMeta) => void;
}

export function TabRecommended({ search, onDetails, onInstall }: Props) {
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [configSkill, setConfigSkill] = useState<SkillMeta | null>(null);

  useEffect(() => {
    api.get<any>('/cloud/skills-pack/tier2/recommended')
      .then((r: any) => {
        const list = r?.skills || r?.data?.skills || r?.data || [];
        setSkills(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = skills.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.description_pt || s.description || '').toLowerCase().includes(q);
  });

  // Group by category
  const byCategory = filtered.reduce<Record<string, SkillMeta[]>>((acc, s) => {
    const cat = s.category || 'Geral';
    (acc[cat] = acc[cat] || []).push(s);
    return acc;
  }, {});

  if (loading) return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ height: '200px', borderRadius: '16px', background: `linear-gradient(90deg, ${T.warningBgSubtle} 25%, ${T.warningBg} 50%, ${T.warningBgSubtle} 75%)`, backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.5s infinite' }} />
      ))}
    </div>
  );

  if (filtered.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: T.textSecondary }}>
 <div style={{ fontSize: '48px', marginBottom: '12px' }}></div>
      <div style={{ fontSize: '16px', fontWeight: 600 }}>Nenhuma skill recomendada encontrada</div>
    </div>
  );

  return (
    <div>
      {Object.entries(byCategory).map(([cat, list]) => (
        <div key={cat} style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: T.textSecondary, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cat}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {list.map((s, i) => (
              <SkillCardNew key={s.name} skill={{ ...s, tier: 'tier2' }} index={i} onDetails={onDetails} onInstall={onInstall} />
            ))}
          </div>
        </div>
      ))}

      {/* Config modal */}
      {configSkill && (
        <>
          <div onClick={() => setConfigSkill(null)} style={{ position: 'fixed', inset: 0, background: T.bgOverlay, zIndex: 2000 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '420px', maxWidth: '90vw', background: T.bg,
            border: `1px solid ${T.warningBgStrong}`, borderRadius: '16px',
            padding: '24px', zIndex: 2001, animation: 'fadeInUp 0.3s ease',
          }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: T.text, marginBottom: '16px' }}>Como Configurar: {configSkill.name}</div>
            <p style={{ fontSize: '13px', color: T.textSecondary, lineHeight: 1.6 }}>
              Esta skill requer: <strong style={{ color: T.warning }}>{configSkill.required_keys || 'API Key'}</strong>
              <br /><br />
              1. Obtenha sua API key no painel do provedor<br />
              2. Vá em Integrações → Adicionar credencial<br />
              3. Cole a key no campo correspondente<br />
              4. A skill será ativada automaticamente
            </p>
            <Button variant="ghost" onClick={() => setConfigSkill(null)} style={{ marginTop: '16px', padding: '10px 20px', borderRadius: '10px', border: `1px solid ${T.warningBgStrong}`, background: T.warningBg, color: T.warning, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Entendi</Button>
          </div>
        </>
      )}
    </div>
  );
}
